import { type FC, useCallback } from "react";

import { Checkbox } from "@jti/core-components/components/Checkbox";
import { Controller, useFormContext } from "@jti/ui";

import type { IsBillingAddressSameAsShippingFieldConfig } from "@checkout-v2/formFields/factories/isBillingAddressSameAsShippingFieldFactory";

export type UseDifferentBillingAddressFieldProps = {
  fieldConfig: IsBillingAddressSameAsShippingFieldConfig;
  onBillingAddressToggle?: (usesDifferentBillingAddress: boolean) => void;
  dataTestId?: string;
};

const setBillingAddressMode = ({
  value,
  fieldName,
  setValue,
  onBillingAddressToggle,
}: {
  value: boolean;
  fieldName: string;
  setValue: (field: string, checked: boolean) => void;
  onBillingAddressToggle?: (usesDifferentBillingAddress: boolean) => void;
}) => {
  const isSameAsShipping = !value;

  setValue(fieldName, isSameAsShipping);

  onBillingAddressToggle?.(value);
};

export const UseDifferentBillingAddressField: FC<UseDifferentBillingAddressFieldProps> = ({
  fieldConfig,
  onBillingAddressToggle,
  dataTestId = "use-different-billing-address-checkbox",
}) => {
  const { control, setValue } = useFormContext();

  const useDifferentLabel = fieldConfig.options.find((option) => !option.value)?.label ?? "";

  const onUseDifferentBillingAddressChange = useCallback(
    (value: boolean) => {
      setBillingAddressMode({
        value,
        fieldName: fieldConfig.name,
        setValue,
        onBillingAddressToggle,
      });
    },
    [fieldConfig.name, onBillingAddressToggle, setValue]
  );

  return (
    <Controller
      control={control}
      name={fieldConfig.name}
      render={({ field }) => (
        <Checkbox
          id={fieldConfig.name}
          name={fieldConfig.name}
          data-testid={dataTestId}
          label={useDifferentLabel}
          checked={field.value === false}
          onCheckedChange={onUseDifferentBillingAddressChange}
        />
      )}
    />
  );
};
